if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      constructor() {
        super();
        this._syncingQuantity = false;
        this._hasScrolledPastButtons = false;
        this._isBarVisible = false;
        this._visibilityFrame = null;
        this.syncContentFromMain = this.syncContentFromMain.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onMainQuantityChange = this.onMainQuantityChange.bind(this);
        this.onStickyQuantityChange = this.onStickyQuantityChange.bind(this);
        this.onButtonsMutation = this.onButtonsMutation.bind(this);
        this.onResize = this.onResize.bind(this);
        this.scheduleVisibilityUpdate =
          this.scheduleVisibilityUpdate.bind(this);
      }

      connectedCallback() {
        if (this.parentElement !== document.body) {
          const existing = document.body.querySelector('product-sticky-bar');
          if (existing && existing !== this) existing.remove();
          document.body.appendChild(this);
          return;
        }

        this.sectionId = this.dataset.sectionId;
        this.buyButtonsTarget = document.getElementById(
          `ProductBuyButtons-${this.sectionId}`,
        );
        this.mainQuantityInput = document.getElementById(
          `Quantity-${this.sectionId}`,
        );
        this.stickyQuantityInput = this.querySelector(
          '.product-sticky-bar__quantity-input',
        );
        this.mainSubmitButton = document.getElementById(
          `ProductSubmitButton-${this.sectionId}`,
        );
        this.mainBuyNowButton = document.getElementById('gokwik-buy-now');
        this.stickyAddToCart = this.querySelector(
          '.product-sticky-bar__add-to-cart',
        );
        this.stickyBuyNow = this.querySelector('.product-sticky-bar__buy-now');
        this.buttonsContainer = this.querySelector(
          '.product-sticky-bar__buttons',
        );
        this.priceContainer = this.querySelector('.product-sticky-bar__price');
        this.mediaImage = this.querySelector('.product-sticky-bar__media');

        if (!this.buyButtonsTarget || !this.mainSubmitButton) {
          this.setAttribute('aria-hidden', 'true');
          return;
        }

        this.setupQuantitySync();
        this.setupButtons();
        this.setupIntersectionObserver();
        this.setupMainButtonObserver();
        this.syncContentFromMain();
        this.scheduleVisibilityUpdate();

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange,
        );

        document.addEventListener('gokwikLoaded', this.syncContentFromMain);
        window.addEventListener('resize', this.onResize);
      }

      onResize() {
        this.scheduleVisibilityUpdate();
        if (this._isBarVisible) {
          this.updateStickyBarDocumentState(true);
        }
      }

      disconnectedCallback() {
        if (this._visibilityFrame) {
          cancelAnimationFrame(this._visibilityFrame);
        }

        this.updateStickyBarDocumentState(false);
        this.intersectionObserver?.disconnect();
        this.mainButtonObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        document.removeEventListener('gokwikLoaded', this.syncContentFromMain);
        window.removeEventListener('resize', this.onResize);
        this.mainQuantityInput?.removeEventListener(
          'change',
          this.onMainQuantityChange,
        );
        this.stickyQuantityInput?.removeEventListener(
          'change',
          this.onStickyQuantityChange,
        );
      }

      setupQuantitySync() {
        if (!this.mainQuantityInput || !this.stickyQuantityInput) return;

        this.mainQuantityInput.addEventListener(
          'change',
          this.onMainQuantityChange,
        );
        this.stickyQuantityInput.addEventListener(
          'change',
          this.onStickyQuantityChange,
        );
      }

      onMainQuantityChange() {
        if (this._syncingQuantity) return;
        this.syncQuantityFromMain();
      }

      onStickyQuantityChange() {
        if (this._syncingQuantity || !this.mainQuantityInput) return;
        this._syncingQuantity = true;
        this.mainQuantityInput.value = this.stickyQuantityInput.value;
        this.mainQuantityInput.dispatchEvent(
          new Event('change', { bubbles: true }),
        );
        this._syncingQuantity = false;
      }

      syncQuantityFromMain() {
        if (!this.mainQuantityInput || !this.stickyQuantityInput) return;

        this._syncingQuantity = true;

        [
          'data-cart-quantity',
          'data-min',
          'data-max',
          'min',
          'max',
          'step',
        ].forEach((attribute) => {
          const value = this.mainQuantityInput.getAttribute(attribute);
          if (value !== null) {
            this.stickyQuantityInput.setAttribute(attribute, value);
          } else {
            this.stickyQuantityInput.removeAttribute(attribute);
          }
        });

        this.stickyQuantityInput.value = this.mainQuantityInput.value;
        this.stickyQuantityInput
          .closest('quantity-input')
          ?.validateQtyRules?.();

        this._syncingQuantity = false;
      }

      setupButtons() {
        this.stickyAddToCart?.addEventListener('click', () => {
          if (
            !this.mainSubmitButton ||
            this.mainSubmitButton.disabled ||
            this.mainSubmitButton.getAttribute('aria-disabled') === 'true'
          ) {
            return;
          }
          this.mainSubmitButton.click();
        });

        this.stickyBuyNow?.addEventListener('click', () => {
          if (
            !this.mainBuyNowButton ||
            this.mainBuyNowButton.disabled ||
            this.mainBuyNowButton.classList.contains('disabled')
          ) {
            return;
          }

          if (typeof window.onBuyNow === 'function') {
            window.onBuyNow(this.mainBuyNowButton);
          } else {
            this.mainBuyNowButton.click();
          }
        });
      }

      setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this._latestObserverEntry = entries[0];
            this.scheduleVisibilityUpdate();
          },
          { threshold: 0 },
        );

        this.intersectionObserver.observe(this.buyButtonsTarget);
      }

      scheduleVisibilityUpdate() {
        if (this._visibilityFrame) return;

        this._visibilityFrame = requestAnimationFrame(() => {
          this._visibilityFrame = null;

          const entry = this._latestObserverEntry;
          const rect =
            entry?.boundingClientRect ??
            this.buyButtonsTarget.getBoundingClientRect();

          this._hasScrolledPastButtons = rect.bottom <= 0;
          this.applyVisibility();
        });
      }

      setupMainButtonObserver() {
        const observeTarget =
          this.mainSubmitButton?.closest('.product-form__buttons') ||
          this.mainSubmitButton;

        if (!observeTarget) return;

        this.mainButtonObserver = new MutationObserver(
          this.onButtonsMutation,
        );
        this.mainButtonObserver.observe(observeTarget, {
          attributes: true,
          subtree: true,
          attributeFilter: ['disabled', 'class', 'style'],
        });
      }

      onButtonsMutation() {
        this.updateButtons();

        if (this._isBarVisible) return;

        this.applyVisibility();
      }

      isProductAvailable() {
        return this.mainSubmitButton && !this.mainSubmitButton.disabled;
      }

      isBuyNowActive() {
        if (!this.mainBuyNowButton) return false;

        return (
          !this.mainBuyNowButton.disabled &&
          !this.mainBuyNowButton.classList.contains('disabled') &&
          this.mainBuyNowButton.offsetWidth > 0 &&
          this.mainBuyNowButton.offsetHeight > 0
        );
      }

      updateButtons() {
        const buyNowActive = this.isBuyNowActive();
        const addToCartActive = this.isProductAvailable();

        if (this.stickyBuyNow) {
          this.stickyBuyNow.hidden = !buyNowActive;
        }

        if (this.stickyAddToCart) {
          this.stickyAddToCart.hidden = !addToCartActive && buyNowActive;
        }

        this.buttonsContainer?.classList.toggle(
          'product-sticky-bar__buttons--single',
          !(buyNowActive && addToCartActive),
        );

        const mainLabel = this.mainSubmitButton?.querySelector('span');
        const stickyLabel = this.stickyAddToCart?.querySelector('span');
        if (mainLabel && stickyLabel) {
          stickyLabel.textContent = mainLabel.textContent;
        }

        this.stickyAddToCart?.classList.toggle(
          'loading',
          this.mainSubmitButton?.classList.contains('loading') ?? false,
        );
        this.stickyAddToCart?.toggleAttribute(
          'aria-disabled',
          this.mainSubmitButton?.getAttribute('aria-disabled') === 'true',
        );
      }

      syncPriceFromMain() {
        const priceSource = document.getElementById(`price-${this.sectionId}`);
        if (!priceSource || !this.priceContainer) return;

        const nextHtml = priceSource.innerHTML;
        if (this.priceContainer.innerHTML !== nextHtml) {
          this.priceContainer.innerHTML = nextHtml;
        }
      }

      updateMedia(variant) {
        if (!this.mediaImage || !variant?.featured_media?.preview_image?.src) {
          return;
        }

        const preview = variant.featured_media.preview_image;
        const url = new URL(preview.src, window.location.origin);
        url.searchParams.set('width', '120');
        const nextSrc = url.toString();

        if (this.mediaImage.src !== nextSrc) {
          this.mediaImage.src = nextSrc;
        }

        if (preview.alt) {
          this.mediaImage.alt = preview.alt;
        }
      }

      syncContentFromMain() {
        this.syncQuantityFromMain();
        this.syncPriceFromMain();
        this.updateButtons();
      }

      onVariantChange({ data: { sectionId, variant } }) {
        if (sectionId !== this.sectionId) return;

        this.syncPriceFromMain();
        this.syncQuantityFromMain();
        this.updateMedia(variant);
        this.updateButtons();
        this.applyVisibility();
      }

      applyVisibility() {
        const shouldShow =
          this.isProductAvailable() && this._hasScrolledPastButtons;

        if (shouldShow === this._isBarVisible) return;

        this._isBarVisible = shouldShow;

        if (!shouldShow) {
          this.classList.remove('is-visible');
          this.setAttribute('aria-hidden', 'true');
          this.updateStickyBarDocumentState(false);
          return;
        }

        this.syncContentFromMain();
        this.setAttribute('aria-hidden', 'false');
        this.classList.add('is-visible');
        this.updateStickyBarDocumentState(true);
      }

      updateStickyBarDocumentState(isVisible) {
        if (isVisible) {
          document.body.classList.add('product-sticky-bar-visible');
          document.documentElement.style.setProperty(
            '--product-sticky-bar-height',
            `${this.offsetHeight}px`,
          );
          return;
        }

        document.body.classList.remove('product-sticky-bar-visible');
        document.documentElement.style.removeProperty(
          '--product-sticky-bar-height',
        );
      }
    },
  );
}
